// The hunt strip: the live hunt under the nav on every page but Hunt. The hunt runs on the server whenever the tab
// is connected, and every field update re-renders this strip, so it moves on any page: state, the wild, the lead
// with HP and XP bars (exact values on hover), a gain that pops on each kill, and live per-hour rates from the
// last ten minutes (hud.ts liveRates, the same events as the session panel). Click anywhere on it to go back.
import { SPECIES } from '../shared/species.ts';
import { GROUND, AREA } from '../shared/grounds.ts';
import { creatureXpToNext, trainerXpToNext } from '../shared/xp.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl } from './assets.ts';
import { lastKillXp, sessionCaught, liveRates, killCount, type LiveRates } from './hud.ts';
import { esc, num, full, cname, duration, estimate } from './fmt.ts';
import { patch } from './dom.ts';
import { placeBeside } from './tip.ts';

type Tip = 'hp' | 'xp' | 'rate';
let tipFor: Tip | null = null;
let tipAnchor: HTMLElement | null = null;
let tipEl: HTMLElement | null = null;

function leadOf(): CreatureDTO | undefined {
  const t = S.trainer!;
  const f = S.field;
  return f?.lead ? S.creatures.get(f.lead.id) : S.creatures.get(t.team[0]);
}

const row = (k: string, v: string) => `<span>${k}</span><b>${v}</b>`;

function tipHtml(kind: Tip): string {
  const lead = leadOf();
  const r = liveRates(lead?.id);
  if (kind === 'rate') {
    if (!r) return '<p class="muted">Rates start after the first minute of hunting.</p>';
    const t = S.trainer!;
    return `<h5>${r.rolling ? 'Last 10 minutes' : `This session (${duration(r.spanMs).replace(/^~/, '')})`}</h5><div class="sp-grid">
      ${row('Trainer XP', `${full(r.xp)}/h`)}${row('Defeated', `${full(r.kills)}/h`)}${row('Coins, net', `${full(r.coins)}/h`)}
      ${r.kills ? row('Per kill', `${(3600 / r.kills).toFixed(1)} s`) : ''}
      ${r.xp > 0 ? row(`Trainer Lv ${t.level + 1}`, `in ${duration(((trainerXpToNext(t.level) - t.xp) / r.xp) * 3_600_000)}`) : ''}</div>`;
  }
  if (!lead) return '';
  if (kind === 'hp') {
    const hp = S.field?.teamHp[lead.id];
    const v = hp ? hp.hp : null, max = hp ? hp.maxHp : null;
    return `<h5>${esc(cname(lead))} · HP</h5><div class="sp-grid">${v !== null && max ? `${row('Health', `${full(v)} / ${full(max)}`)}${row('Share', `${Math.round((v / max) * 100)}%`)}` : row('Health', 'full')}</div>`;
  }
  const need = creatureXpToNext(lead.level);
  const togo = Math.max(0, need - lead.xp);
  const rate = r?.lead ?? null;
  return `<h5>${esc(cname(lead))} · Lv ${lead.level}</h5><div class="sp-grid">
    ${row('XP', `${full(lead.xp)} / ${full(need)}`)}${row(`To Lv ${lead.level + 1}`, full(togo))}
    ${row('Last kill', lastKillXp() ? `+${full(lastKillXp())}` : '—')}
    ${rate ? `${row('Rate', `${full(rate)} XP/h`)}${row('Next level', `in ${duration((togo / rate) * 3_600_000)}`)}` : row('Next level', 'after a few kills')}</div>`;
}

function placeTip() {
  if (!tipEl || !tipAnchor || !tipFor) return;
  if (!tipAnchor.isConnected) { hideTip(); return; }
  tipEl.innerHTML = tipHtml(tipFor);
  placeBeside(tipEl, tipAnchor, { prefer: 'below', gap: 8 });
}
function showTip(el: HTMLElement) {
  if (!tipEl) { tipEl = document.createElement('div'); tipEl.className = 'strip-pop'; tipEl.setAttribute('role', 'tooltip'); document.body.appendChild(tipEl); }
  tipFor = el.dataset.sp as Tip; tipAnchor = el;
  tipEl.hidden = false;
  placeTip();
}
function hideTip() { tipFor = null; tipAnchor = null; if (tipEl) tipEl.hidden = true; }

let mounted = false;
function mount(host: HTMLElement) {
  if (mounted) return;
  mounted = true;
  const find = (e: Event) => (e.target as HTMLElement).closest?.('[data-sp]') as HTMLElement | null;
  host.addEventListener('mouseover', (e) => { const el = find(e); if (el && el !== tipAnchor) showTip(el); });
  host.addEventListener('mouseout', (e) => { const to = (e as MouseEvent).relatedTarget as Node | null; if (tipAnchor && !(to && tipAnchor.contains(to))) hideTip(); });
  host.addEventListener('focusin', (e) => { const el = find(e); if (el) showTip(el); });
  host.addEventListener('focusout', hideTip);
  window.addEventListener('scroll', hideTip, true);
}

function rates(r: LiveRates | null): string {
  if (!r) return '';
  const v = (x: number, unit: string, cls: string) => `<span class="srt ${cls}"><b>${num(x)}</b> ${unit}</span>`;
  return `<span class="srates" data-sp="rate" tabindex="0" aria-label="Hunt rates">${v(r.xp, 'XP/h', 'srt-xp')}${v(r.kills, 'kills/h', 'srt-k')}${v(r.coins, 'coins/h', 'srt-c')}</span>`;
}

export function renderStrip() {
  const host = document.getElementById('strip');
  if (!host || !S.trainer) return;
  mount(host);
  const t = S.trainer;
  const f = S.field;
  const g = GROUND[t.ground];
  const lead = leadOf();
  const resting = f && f.restingUntil > serverNow();
  const state = !S.online ? '<span class="live off">Paused · offline</span>' : t.stopped ? '<span class="live off">In Kinhall</span>' : resting ? `<span class="live rest">Resting ${Math.ceil((f!.restingUntil - serverNow()) / 1000)} s</span>` : '<span class="live">Hunting</span>';
  const hp = lead ? f?.teamHp[lead.id] : undefined;
  const hpPct = hp ? (hp.hp / hp.maxHp) * 100 : 100;
  const xpPct = lead ? Math.min(100, (lead.xp / creatureXpToNext(lead.level)) * 100) : 0;
  const gain = lastKillXp();
  const n = killCount();
  const r = liveRates(lead?.id);
  const spk = !r && lead && g ? `<span class="muted ellip">${estimate(lead, g).secondsPerKill.toFixed(1)} s/kill</span>` : '';
  patch(host, `${state}
    ${g ? `<img class="sp" src="${spriteUrl(g.species)}" alt=""><span class="ellip"><b>${esc(SPECIES[g.species].name)}</b> <span class="muted">Lv ${g.level}<span class="sarea"> · ${esc(AREA[g.area].name)}</span></span></span>` : ''}
    <span class="divider"></span>
    ${lead ? `<img class="sp" src="${spriteUrl(lead.species, lead.rolls.aura)}" alt=""><span class="ellip"><b>${esc(cname(lead))}</b> <span class="muted">Lv ${lead.level}</span></span>
      <span class="bars"><span class="thin hp${hpPct < 35 ? ' low' : ''}" data-sp="hp" tabindex="0" aria-label="HP"><i style="width:${hpPct}%"></i></span><span class="thin xp" data-sp="xp" tabindex="0" aria-label="XP"><i style="width:${xpPct}%"></i></span></span>` : ''}
    <span class="gain-wrap">${gain ? `<span class="gain">+${full(gain)} XP</span><i class="gain-pop" data-key="k${n}" aria-hidden="true">+${full(gain)}</i>` : ''}</span>
    <span class="divider"></span>
    ${rates(r)}${spk}
    <span class="muted ellip caught">${full(sessionCaught())} caught</span>
    <span class="spacer"></span>
    <a class="btn small back" href="#/hunt">Back to hunt</a>`);
  if (tipFor) placeTip();
}
